import React, { useEffect, useMemo, useState } from 'react';
import SubPageLayout from '../helpers/SubPageLayout';
import { Button, Modal, Input, Avatar, Row, Col, Tag, Divider, Spin, Tooltip } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import MyTable from '../helpers/MyTable';
import { ActionsColumn, DetailsCard } from '../helpers/Components';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { isFeatureEnabled, getIsSuperAdmin, getUser, getIsAdminApi } from '../../../features/auth.store';
import {
  getCompanyLoading,
  getCompanyDetailsById,
  getSelectedCompany,
  setSelectedCompany,
} from '../../../features/company.store';
import { DEFAULT_PAGE_SIZE, UPDATE_ON_FORMAT } from '../../../utils/constant';
import { GET } from '../../../service/base-api-service';
import { ROLE } from '../../../utils/features';
import get from 'lodash.get';
import { resizeCloudinaryImages } from '../../../utils/utils';
import {
  CompanyDetailAdminEditors,
  CompanyDetailDescriptionEditors,
  CompanyDetailImageEditors,
  CompanyDetailInfoEditors,
} from '../helpers/company-detail-editors';

const normalizeRoles = (user) => {
  const scope = get(user, 'scope');
  const role = get(user, 'role');
  const roles = get(user, 'roles', []);
  const authorities = get(user, 'authorities', []);
  const authorityNames = Array.isArray(authorities)
    ? authorities.map((a) => (typeof a === 'string' ? a : a?.authority)).filter(Boolean)
    : [];

  const splitScope =
    typeof scope === 'string' ? scope.split(',').map((s) => s.trim()).filter(Boolean) : [];

  return Array.from(
    new Set([
      ...splitScope,
      ...(role ? [role] : []),
      ...(Array.isArray(roles) ? roles : []),
      ...authorityNames,
    ].filter(Boolean)),
  );
};

const hasRole = (user, roleConst) => normalizeRoles(user).includes(roleConst);

const setSearchFocusStyle = (event, focused) => {
  const wrapper = event.currentTarget.closest('.ant-input-affix-wrapper');
  if (!wrapper) return;

  if (focused) {
    wrapper.style.setProperty('border-color', '#009548', 'important');
    wrapper.style.setProperty('box-shadow', '0 0 0 2px rgba(0, 149, 72, 0.12)', 'important');
  } else {
    wrapper.style.removeProperty('border-color');
    wrapper.style.removeProperty('box-shadow');
  }
};

const SectionHeader = ({ title, extra }) => (
  <div style={styles.sectionHeader}>
    <div style={styles.sectionTitle}>{title}</div>
    {extra ? <div style={styles.sectionExtra}>{extra}</div> : null}
  </div>
);

const InfoItem = ({ icon, label, value }) => (
  <div style={styles.infoItem}>
    <div style={styles.infoIcon}>{icon}</div>
    <div style={styles.infoContent}>
      <div style={styles.infoLabel}>{label}</div>
      <div style={styles.infoValue}>{value || '-'}</div>
    </div>
  </div>
);

const columns = ({ handleChangeOrder, localAscending, handleOpenCompany }) => [
  {
    title: 'Company name',
    dataIndex: 'name',
    render: (name, record) => (
      <div
        onClick={(e) => {
          e.stopPropagation();
          handleOpenCompany(record);
        }}
        style={{ cursor: 'pointer', display: 'inline-block' }}
      >
        <DetailsCard
          key={record.identifier}
          name={name}
          src={record.imageUrl}
          className="d-flex f-row ai-center"
          as="div"
        />
      </div>
    ),
    sorter: true,
    sortDirections: [localAscending ? 'descend' : 'ascend'],
    onHeaderCell: () => ({
      onClick: handleChangeOrder,
      style: { cursor: 'pointer' },
    }),
  },
  {
    title: 'Admin Name',
    dataIndex: ['admin', 'fullName'],
    render: (value) => value || '-',
  },
  {
    title: 'Update On',
    dataIndex: 'lastModifiedDate',
    render: (data) => (data ? format(new Date(data), UPDATE_ON_FORMAT) : '-'),
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 110,
    render: (value, record) => (
      <div className="company-row-actions" onClick={(event) => event.stopPropagation()}>
        <ActionsColumn showEdit editUrl={`/companies/${record.identifier}/details`} />
        <Tooltip title="View company">
          <Button
            type="link"
            className="company-view-action"
            aria-label={`View ${record.name}`}
            icon={<EyeOutlined />}
            onClick={() => handleOpenCompany(record)}
          />
        </Tooltip>
      </div>
    ),
  },
];

const Companies = () => {
  const getFeatureEnabled = useSelector(isFeatureEnabled);
  const featureAddCompany = getFeatureEnabled('add_company');

  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const isSuperAdmin = useSelector(getIsSuperAdmin);
  const isAdminApi = useSelector(getIsAdminApi);

  const companyListLoading = useSelector(getCompanyLoading);
  const selectedCompany = useSelector(getSelectedCompany);

  const [allCompanies, setAllCompanies] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [localAscending, setLocalAscending] = useState(true);

  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const isMinearcRole = useMemo(() => hasRole(user, ROLE.MINEARC), [user]);
  const isSuperAdminRole = useMemo(() => hasRole(user, ROLE.SA), [user]);
  const hasGlobalCompanyAccess = useMemo(
    () => isSuperAdmin || isSuperAdminRole || isMinearcRole || isAdminApi,
    [isSuperAdmin, isSuperAdminRole, isMinearcRole, isAdminApi],
  );

  useEffect(() => {
    const loadCompanies = async () => {
      setGlobalLoading(true);
      try {
        const payload = await GET('v1/admin/tenant', {
          params: {
            page: 0,
            size: 10000,
            sortColumn: 'name',
            isAscending: true,
            searchKey: '',
          },
        });

        setAllCompanies(payload?.results || []);
      } catch (err) {
        console.error('COMPANIES: failed to load companies', err);
        setAllCompanies([]);
      } finally {
        setGlobalLoading(false);
      }
    };

    if (hasGlobalCompanyAccess) {
      loadCompanies();
    }
  }, [hasGlobalCompanyAccess]);

  const filteredCompanies = useMemo(() => {
    let list = [...(allCompanies || [])];

    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();

      list = list.filter((company) => {
        const name = company?.name?.toLowerCase() || '';
        const adminName = company?.admin?.fullName?.toLowerCase() || '';
        const email = company?.email?.toLowerCase() || '';
        const phone = company?.phone?.toLowerCase() || '';
        const address = company?.address?.toLowerCase() || '';

        return (
          name.includes(q) ||
          adminName.includes(q) ||
          email.includes(q) ||
          phone.includes(q) ||
          address.includes(q)
        );
      });
    }

    list.sort((a, b) => {
      const aName = (a?.name || '').toLowerCase();
      const bName = (b?.name || '').toLowerCase();

      if (localAscending) return aName.localeCompare(bName);
      return bName.localeCompare(aName);
    });

    return list;
  }, [allCompanies, searchText, localAscending]);

  const paginatedCompanies = useMemo(() => {
    const start = (currentPage - 1) * DEFAULT_PAGE_SIZE;
    const end = start + DEFAULT_PAGE_SIZE;
    return filteredCompanies.slice(start, end);
  }, [filteredCompanies, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const handleChangeOrder = () => {
    setLocalAscending((prev) => !prev);
  };

  const handleOpenCompany = (company) => {
    setSelectedCompanyId(company?.identifier);
    setCompanyModalOpen(true);

    if (company) {
      dispatch(setSelectedCompany(company));
    }

    if (company?.identifier) {
      dispatch(getCompanyDetailsById({ id: company.identifier }));
    }
  };

  const handleCloseCompany = () => {
    setCompanyModalOpen(false);
    setSelectedCompanyId(null);
  };

  const modalCompany = selectedCompany?.identifier === selectedCompanyId ? selectedCompany : null;
  const activeLoading = hasGlobalCompanyAccess ? globalLoading : companyListLoading;
  const modalLoading = companyModalOpen && (!modalCompany || companyListLoading);

  return (
    <SubPageLayout
      title="Companies"
      extra={
        featureAddCompany && (
          <Link to="/companies/add">
            <Button type="primary" icon={<PlusOutlined />}>
              Add Company
            </Button>
          </Link>
        )
      }
    >
      <div style={styles.filterBar}>
        <div style={styles.searchWrap}>
          <Input
            allowClear
            size="large"
            prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />}
            placeholder="Search Companies"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onFocus={(event) => setSearchFocusStyle(event, true)}
            onBlur={(event) => setSearchFocusStyle(event, false)}
          />
        </div>
      </div>

      <MyTable
        columns={columns({
          handleChangeOrder,
          localAscending,
          handleOpenCompany,
        })}
        dataSource={paginatedCompanies}
        pagination={{
          position: ['none', 'bottomCenter'],
          pageSize: DEFAULT_PAGE_SIZE,
          total: filteredCompanies.length,
          current: currentPage,
        }}
        onChange={(pagination) => setCurrentPage(pagination.current)}
        loading={activeLoading}
        rowKey="identifier"
      />

      <Modal
        visible={companyModalOpen}
        onCancel={handleCloseCompany}
        footer={null}
        width={1280}
        destroyOnClose={false}
        bodyStyle={{ padding: 0 }}
        style={{ top: 18 }}
      >
        <div style={styles.modalWrap}>
          {modalLoading ? (
            <div style={styles.modalLoadingWrap}>
              <Spin size="large" />
            </div>
          ) : modalCompany ? (
            <>
              <div style={styles.modalHero}>
                <div style={styles.modalHeroLeft}>
                  <div style={styles.heroImageWrap}>
                    <Avatar
                      shape="square"
                      size={108}
                      src={
                        modalCompany.imageUrl
                          ? resizeCloudinaryImages(modalCompany.imageUrl, 220, 220)
                          : undefined
                      }
                      style={styles.heroAvatar}
                    >
                      {(modalCompany.name || 'C').charAt(0)}
                    </Avatar>
                    <div style={styles.heroImageEdit}>
                      <CompanyDetailImageEditors id={modalCompany.identifier} />
                    </div>
                  </div>

                  <div style={styles.heroText}>
                    <div style={styles.heroTopRow}>
                      <div style={styles.heroTitleBlock}>
                        <div style={styles.heroTitle}>{modalCompany.name || 'Company Details'}</div>
                        <div style={styles.heroSubtitle}>
                          {modalCompany?.admin?.fullName || 'No Admin Assigned'}
                        </div>
                      </div>

                      <Tag color={modalCompany.isEnabled ? 'green' : 'default'} style={styles.statusTag}>
                        {modalCompany.isEnabled ? 'Active' : 'Disabled'}
                      </Tag>
                    </div>

                    <div style={styles.heroMetaRow}>
                      <div style={styles.heroMetaItem}>
                        <MailOutlined />
                        <span>{modalCompany.email || 'No email'}</span>
                      </div>
                      <div style={styles.heroMetaItem}>
                        <PhoneOutlined />
                        <span>{modalCompany.phone || 'No phone'}</span>
                      </div>
                      <div style={styles.heroMetaItem}>
                        <CalendarOutlined />
                        <span>
                          {modalCompany.lastModifiedDate
                            ? format(new Date(modalCompany.lastModifiedDate), UPDATE_ON_FORMAT)
                            : 'No update date'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.modalBody}>
                <Row gutter={[20, 20]}>
                  <Col xs={24} xl={8}>
                    <div style={styles.sectionCard}>
                      <SectionHeader
                        title="Company Information"
                        extra={<CompanyDetailInfoEditors company={modalCompany} />}
                      />

                      <Divider style={{ margin: '10px 0 16px' }} />

                      <div style={styles.infoGrid}>
                        <InfoItem
                          icon={<UserOutlined />}
                          label="Company Name"
                          value={modalCompany.name}
                        />
                        <InfoItem
                          icon={<MailOutlined />}
                          label="Email Address"
                          value={modalCompany.email}
                        />
                        <InfoItem
                          icon={<PhoneOutlined />}
                          label="Phone"
                          value={modalCompany.phone}
                        />
                        <InfoItem
                          icon={<EnvironmentOutlined />}
                          label="Address"
                          value={modalCompany.address}
                        />
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} xl={8}>
                    <div style={styles.sectionCard}>
                      <SectionHeader
                        title="Admin Details"
                        extra={<CompanyDetailAdminEditors company={modalCompany} />}
                      />

                      <Divider style={{ margin: '10px 0 16px' }} />

                      <div style={styles.infoGrid}>
                        <InfoItem
                          icon={<UserOutlined />}
                          label="Name"
                          value={modalCompany?.admin?.fullName}
                        />
                        <InfoItem
                          icon={<MailOutlined />}
                          label="Email Address"
                          value={modalCompany?.admin?.email}
                        />
                        <InfoItem
                          icon={<PhoneOutlined />}
                          label="Phone"
                          value={modalCompany?.admin?.telephoneNumber}
                        />
                        <InfoItem
                          icon={<UserOutlined />}
                          label="Company Role"
                          value={modalCompany?.admin?.role?.displayName}
                        />
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} xl={8}>
                    <div style={styles.sectionCard}>
                      <SectionHeader
                        title="Company Description"
                        extra={<CompanyDetailDescriptionEditors company={modalCompany} />}
                      />

                      <Divider style={{ margin: '10px 0 16px' }} />

                      {modalCompany.description ? (
                        <div
                          style={styles.descriptionContent}
                          dangerouslySetInnerHTML={{ __html: modalCompany.description }}
                        />
                      ) : (
                        <div style={styles.emptyDescription}>No description available.</div>
                      )}
                    </div>
                  </Col>

                  <Col span={24}>
                    <div style={styles.bottomBar}>
                      <div style={styles.bottomStatus}>
                        <Tag color={modalCompany.isEnabled ? 'green' : 'default'} style={{ margin: 0 }}>
                          {modalCompany.isEnabled ? 'Company Enabled' : 'Company Disabled'}
                        </Tag>
                      </div>

                      <div style={styles.bottomActions}>
                        <Button onClick={handleCloseCompany} size="large">
                          Close
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          ) : (
            <div style={styles.modalLoadingWrap}>
              <div>No company found.</div>
            </div>
          )}
        </div>
      </Modal>
    </SubPageLayout>
  );
};

export default Companies;

const styles = {
  filterBar: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap',
  },

  searchWrap: {
    flex: 1,
    minWidth: 280,
    maxWidth: 460,
  },

  modalWrap: {
    background: '#f7f9fb',
    borderRadius: 20,
    overflow: 'hidden',
    minHeight: 720,
  },

  modalLoadingWrap: {
    minHeight: 720,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f7f9fb',
  },

  modalHero: {
    padding: '28px 30px 24px',
    background: 'linear-gradient(135deg, #ffffff 0%, #f4f8f5 100%)',
    borderBottom: '1px solid #edf1f5',
  },

  modalHeroLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    flexWrap: 'wrap',
  },

  heroImageWrap: {
    position: 'relative',
    flexShrink: 0,
  },

  heroAvatar: {
    background: '#e8f5ec',
    color: '#0b6a3b',
    fontWeight: 800,
    fontSize: 30,
    border: '4px solid #ffffff',
    boxShadow: '0 12px 30px rgba(16, 24, 40, 0.12)',
  },

  heroImageEdit: {
    position: 'absolute',
    right: -6,
    bottom: -6,
    background: '#fff',
    borderRadius: 12,
    padding: 4,
    boxShadow: '0 8px 20px rgba(16, 24, 40, 0.10)',
  },

  heroText: {
    minWidth: 0,
    flex: 1,
  },

  heroTopRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },

  heroTitleBlock: {
    minWidth: 0,
    flex: 1,
  },

  heroTitle: {
    fontSize: 30,
    fontWeight: 800,
    color: '#17212b',
    lineHeight: 1.15,
  },

  heroSubtitle: {
    marginTop: 8,
    fontSize: 15,
    color: '#5b6570',
    fontWeight: 500,
  },

  statusTag: {
    margin: 0,
    fontWeight: 700,
    paddingInline: 12,
    height: 30,
    lineHeight: '28px',
    borderRadius: 999,
  },

  heroMetaRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 18,
  },

  heroMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    background: '#fff',
    border: '1px solid #ecf0f3',
    borderRadius: 12,
    color: '#475467',
    fontSize: 13,
    fontWeight: 600,
  },

  modalBody: {
    padding: 24,
  },

  sectionCard: {
    background: '#fff',
    border: '1px solid #edf1f5',
    borderRadius: 18,
    padding: 20,
    height: '100%',
    boxShadow: '0 4px 18px rgba(16, 24, 40, 0.05)',
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },

  sectionTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: '#17212b',
  },

  sectionExtra: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },

  infoGrid: {
    display: 'grid',
    gap: 14,
  },

  infoItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 14,
    background: '#fafcfd',
    border: '1px solid #eef2f6',
    borderRadius: 14,
  },

  infoIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: 'rgba(11, 106, 59, 0.08)',
    color: '#0b6a3b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: 16,
  },

  infoContent: {
    minWidth: 0,
    flex: 1,
  },

  infoLabel: {
    fontSize: 12,
    color: '#7b8794',
    marginBottom: 4,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },

  infoValue: {
    fontSize: 15,
    color: '#1f2933',
    fontWeight: 600,
    wordBreak: 'break-word',
  },

  descriptionContent: {
    fontSize: 14,
    color: '#2f3a45',
    lineHeight: 1.75,
    minHeight: 320,
    maxHeight: 420,
    overflowY: 'auto',
    paddingRight: 4,
  },

  emptyDescription: {
    color: '#7b8794',
    fontStyle: 'italic',
    minHeight: 180,
    display: 'flex',
    alignItems: 'center',
  },

  bottomBar: {
    background: '#fff',
    border: '1px solid #edf1f5',
    borderRadius: 18,
    padding: 18,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },

  bottomStatus: {
    display: 'flex',
    alignItems: 'center',
  },

  bottomActions: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
};